import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  Upload,
  Download,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  ArrowRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Importar() {
  const navigate = useNavigate();
  const { canCreate } = useAuth();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  if (!canCreate()) {
    navigate('/dashboard');
    return null;
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
        toast.error('Selecione um arquivo Excel (.xlsx ou .xls)');
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/codes/export/template`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_importacao_tecnoservice.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template baixado com sucesso!');
    } catch (error) {
      toast.error('Erro ao baixar template');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Selecione um arquivo');
      return;
    }

    setUploading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/api/codes/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setResult(response.data);
      
      if (response.data.success > 0) {
        toast.success(`${response.data.success} códigos importados com sucesso!`);
      }
      if (response.data.errors.length > 0) {
        toast.warning(`${response.data.errors.length} erros encontrados`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar arquivo');
    } finally {
      setUploading(false);
    }
  };

  const successRate = result ? Math.round((result.success / result.total) * 100) : 0;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="importar-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
            Importar Códigos em Massa
          </h1>
          <p className="text-slate-500 mt-1">
            Importe múltiplos códigos de uma planilha Excel
          </p>
        </div>

        {/* Template Download */}
        <Card className="border-slate-200 shadow-none border-dashed">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-50 rounded-md">
                  <FileSpreadsheet className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-medium text-slate-900">Template de Importação</h3>
                  <p className="text-sm text-slate-500">
                    Baixe o modelo Excel com as instruções de preenchimento
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={handleDownloadTemplate}
                className="border-blue-200 text-blue-600 hover:bg-blue-50"
                data-testid="download-template-btn"
              >
                <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Baixar Template
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Upload Area */}
        <Card className="border-slate-200 shadow-none">
          <CardHeader>
            <CardTitle className="font-heading text-lg font-bold flex items-center gap-2">
              <Upload className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
              Upload de Arquivo
            </CardTitle>
            <CardDescription>
              Selecione uma planilha Excel preenchida com os dados dos materiais
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* File Input */}
            <div
              onClick={() => fileInputRef.current?.click()}
              className={`
                border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                ${file ? 'border-green-300 bg-green-50' : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'}
              `}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
                data-testid="file-input"
              />
              {file ? (
                <div className="flex flex-col items-center gap-2">
                  <CheckCircle className="w-10 h-10 text-green-600" strokeWidth={1.5} />
                  <p className="font-medium text-green-700">{file.name}</p>
                  <p className="text-sm text-green-600">Clique para trocar o arquivo</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="w-10 h-10 text-slate-400" strokeWidth={1.5} />
                  <p className="font-medium text-slate-700">Clique para selecionar arquivo</p>
                  <p className="text-sm text-slate-500">Arquivos .xlsx ou .xls</p>
                </div>
              )}
            </div>

            {/* Upload Button */}
            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white shadow-sm"
              data-testid="upload-btn"
            >
              {uploading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Importando...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Upload className="w-4 h-4" strokeWidth={1.5} />
                  Importar Códigos
                </span>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Results */}
        {result && (
          <Card className="border-slate-200 shadow-none" data-testid="import-result">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg font-bold">
                Resultado da Importação
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Progress */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Progresso</span>
                  <span className="font-medium">{successRate}%</span>
                </div>
                <Progress value={successRate} className="h-2" />
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-slate-50 rounded-md text-center">
                  <p className="text-2xl font-bold text-slate-900">{result.total}</p>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Total</p>
                </div>
                <div className="p-4 bg-green-50 rounded-md text-center">
                  <p className="text-2xl font-bold text-green-600">{result.success}</p>
                  <p className="text-xs text-green-600 uppercase tracking-wider">Sucesso</p>
                </div>
                <div className="p-4 bg-red-50 rounded-md text-center">
                  <p className="text-2xl font-bold text-red-600">{result.errors.length}</p>
                  <p className="text-xs text-red-600 uppercase tracking-wider">Erros</p>
                </div>
              </div>

              {/* Errors List */}
              {result.errors.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" strokeWidth={1.5} />
                    Erros Encontrados
                  </h4>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {result.errors.map((error, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 p-2 bg-red-50 rounded text-sm"
                      >
                        <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" strokeWidth={1.5} />
                        <span className="text-red-700">
                          Linha {error.row}: {error.error}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              {result.success > 0 && (
                <Button
                  onClick={() => navigate('/catalogo')}
                  variant="outline"
                  className="w-full"
                >
                  Ver Catálogo
                  <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
